import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useHospitalOnboarding } from '../../../contexts/HospitalOnboardingContext';
import placesService from '../../../services/placesService';
import {
  Truck,
  Plus,
  Trash2,
  Mic,
  ArrowRight,
  Bookmark,
  ChevronDown,
  Check,
  Loader2,
  MapPin,
  Search,
  X
} from 'lucide-react';
import { getAmbulanceTypeAbbreviation } from '../../../utils/ambulanceTypeHelper';

const PEAK_SLOTS = ['24x7', 'Morning', 'Noon', 'Evening', 'Night'];

/**
 * Compact Area Search Autocomplete Input for Table Cell
 * Fetches Google Places suggestions and auto-fills PIN Code on select
 */
const AreaSearchInput = ({ value, onChange, onSelectPlace }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const isTypingRef = useRef(false);

  useEffect(() => {
    if (value !== query && !isTypingRef.current) {
      setQuery(value || '');
    }
  }, [value]);

  const updateDropdownPosition = () => {
    if (inputRef.current) {
      setDropdownRect(inputRef.current.getBoundingClientRect());
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        !e.target.closest('[data-area-search-portal]')
      ) {
        setIsOpen(false);
      }
    };
    const handleScrollOrResize = () => {
      if (isOpen) {
        updateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isTypingRef.current || query.trim().length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        updateDropdownPosition();
        const res = await placesService.getAutocomplete(query.trim(), { country: 'in' });
        if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
          setSuggestions(res.data);
          setIsOpen(true);
        } else {
          setSuggestions([]);
          setIsOpen(false);
        }
      } catch (err) {
        console.error('Error fetching area suggestions:', err);
        setSuggestions([]);
        setIsOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    isTypingRef.current = true;
    setQuery(val);
    onChange(val);
  };

  const handleSelect = async (suggestion) => {
    isTypingRef.current = false;
    setIsOpen(false);
    setLoading(true);

    let area = suggestion.description || suggestion.mainText;
    let pincode = '';

    try {
      if (suggestion.placeId) {
        const detailsRes = await placesService.getPlaceDetails(suggestion.placeId);
        if (detailsRes?.success && detailsRes.data) {
          const place = detailsRes.data;
          const postalComp = place.addressComponents?.find((c) =>
            c.types?.includes('postal_code')
          );
          if (postalComp?.long_name) {
            pincode = postalComp.long_name;
          } else if (place.formattedAddress) {
            const match = place.formattedAddress.match(/\b\d{6}\b/);
            if (match) pincode = match[0];
          }
          if (place.formattedAddress) {
            area = place.formattedAddress;
          }
        }
      }
    } catch (err) {
      console.warn('Could not fetch place details for postal code:', err);
      const match = (suggestion.description || '').match(/\b\d{6}\b/);
      if (match) pincode = match[0];
    } finally {
      setLoading(false);
    }

    setQuery(area);
    onSelectPlace({ area, pincode });
  };

  const handleClear = () => {
    isTypingRef.current = false;
    setQuery('');
    setSuggestions([]);
    setIsOpen(false);
    onSelectPlace({ area: '', pincode: '' });
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative flex items-center">
        <MapPin className="w-3.5 h-3.5 text-blue-600 absolute left-2.5 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search location / area..."
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            updateDropdownPosition();
            if (suggestions.length > 0) setIsOpen(true);
          }}
          className="w-full py-2 pl-8 pr-7 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 transition-all"
        />
        <div className="absolute right-2 flex items-center gap-1">
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 text-blue-600 animate-spin" />
          ) : query ? (
            <button
              type="button"
              onClick={handleClear}
              className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </div>
      </div>

      {isOpen && dropdownRect && createPortal(
        <div
          data-area-search-portal
          className="bg-white border border-slate-200 rounded-xl shadow-xl max-h-56 overflow-y-auto divide-y divide-slate-100 z-[9999]"
          style={{
            position: 'fixed',
            top: dropdownRect.bottom + 4,
            left: dropdownRect.left,
            width: Math.max(dropdownRect.width, 280)
          }}
        >
          {suggestions.map((item) => (
            <button
              key={item.placeId || item.description}
              type="button"
              onClick={() => handleSelect(item)}
              className="w-full px-3 py-2 text-left hover:bg-blue-50/60 transition-colors flex items-start gap-2 cursor-pointer"
            >
              <MapPin className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-slate-800 truncate">
                  {item.mainText || item.description}
                </p>
                {item.secondaryText && (
                  <p className="text-[11px] text-slate-400 truncate">
                    {item.secondaryText}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export const Step2CapacityCoverage = () => {
  const navigate = useNavigate();
  const { formData, updateFields, saveAndContinue, goBack, saveAndExit, isSaving, ambulanceTypeNames, loadingAmbulanceTypes } = useHospitalOnboarding();

  // Fleet capacity table rows
  const [capacities, setCapacities] = useState(() => {
    if (formData.fleet_capacities && formData.fleet_capacities.length > 0) {
      const valid = formData.fleet_capacities.filter(
        (r) => r.owned !== '8' && r.attached !== '3' && r.available_daily !== '10' && (r.ambulance_type || r.owned || r.attached || r.available_daily)
      );
      if (valid.length > 0) return valid;
    }
    return [
      {
        id: 'fc-1',
        ambulance_type: '',
        owned: '',
        attached: '',
        available_daily: ''
      }
    ];
  });

  // Operating areas table rows
  const [operatingAreas, setOperatingAreas] = useState(() => {
    if (formData.operating_areas && formData.operating_areas.length > 0) {
      const valid = formData.operating_areas.filter(
        (r) => (r.area_pin && r.area_pin !== 'SG Highway / 380054') || r.area || r.pincode
      );
      if (valid.length > 0) {
        return valid.map((r) => {
          let area = r.area || '';
          let pincode = r.pincode || '';
          if (!area && r.area_pin) {
            const pinMatch = r.area_pin.match(/\b\d{6}\b/);
            if (pinMatch) {
              pincode = pinMatch[0];
              area = r.area_pin.replace(/[\/\-]\s*\b\d{6}\b/, '').replace(/\b\d{6}\b/, '').trim();
            } else {
              area = r.area_pin;
            }
          }
          return {
            ...r,
            area,
            pincode,
            area_pin: r.area_pin || (area && pincode ? `${area} / ${pincode}` : (area || pincode || ''))
          };
        });
      }
    }
    return [
      {
        id: 'oa-1',
        area: '',
        pincode: '',
        area_pin: '',
        available_ambulances: '',
        peak_availability: [],
        type_mix: []
      }
    ];
  });

  const handleAddCapacityRow = () => {
    const newRow = {
      id: typeof window !== 'undefined' && window.crypto?.randomUUID ? `fc-${window.crypto.randomUUID()}` : `fc-${Math.random().toString(36).slice(2, 9)}`,
      ambulance_type: '',
      owned: '',
      attached: '',
      available_daily: ''
    };
    const updated = [...capacities, newRow];
    setCapacities(updated);
    updateFields({ fleet_capacities: updated });
  };

  const handleRemoveCapacityRow = (id) => {
    if (capacities.length <= 1) return;
    const updated = capacities.filter((r) => r.id !== id);
    setCapacities(updated);
    updateFields({ fleet_capacities: updated });
  };

  const handleCapacityChange = (id, field, value) => {
    const updated = capacities.map((r) => (r.id === id ? { ...r, [field]: value } : r));
    setCapacities(updated);
    updateFields({ fleet_capacities: updated });
  };

  const handleAddAreaRow = () => {
    const newRow = {
      id: typeof window !== 'undefined' && window.crypto?.randomUUID ? `oa-${window.crypto.randomUUID()}` : `oa-${Math.random().toString(36).slice(2, 9)}`,
      area: '',
      pincode: '',
      area_pin: '',
      available_ambulances: '',
      peak_availability: [],
      type_mix: []
    };
    const updated = [...operatingAreas, newRow];
    setOperatingAreas(updated);
    updateFields({ operating_areas: updated });
  };

  const handleRemoveAreaRow = (id) => {
    if (operatingAreas.length <= 1) return;
    const updated = operatingAreas.filter((r) => r.id !== id);
    setOperatingAreas(updated);
    updateFields({ operating_areas: updated });
  };

  const handleAreaChange = (id, field, value) => {
    const updated = operatingAreas.map((r) => {
      if (r.id !== id) return r;
      const updatedRow = { ...r, [field]: value };
      const area = field === 'area' ? value : (updatedRow.area || '');
      const pin = field === 'pincode' ? value : (updatedRow.pincode || '');
      updatedRow.area_pin = area && pin ? `${area} / ${pin}` : (area || pin || '');
      return updatedRow;
    });
    setOperatingAreas(updated);
    updateFields({ operating_areas: updated });
  };

  const handleAreaSelectPlace = (id, { area, pincode }) => {
    const updated = operatingAreas.map((r) => {
      if (r.id !== id) return r;
      const areaPin = area && pincode ? `${area} / ${pincode}` : (area || pincode || '');
      return {
        ...r,
        area,
        pincode,
        area_pin: areaPin
      };
    });
    setOperatingAreas(updated);
    updateFields({ operating_areas: updated });
  };

  const togglePeakSlot = (id, slot) => {
    const updated = operatingAreas.map((r) => {
      if (r.id !== id) return r;
      const current = r.peak_availability || [];
      const next = current.includes(slot) ? current.filter((s) => s !== slot) : [...current, slot];
      return { ...r, peak_availability: next };
    });
    setOperatingAreas(updated);
    updateFields({ operating_areas: updated });
  };

  const toggleTypeMix = (id, mix) => {
    const updated = operatingAreas.map((r) => {
      if (r.id !== id) return r;
      const current = r.type_mix || [];
      const next = current.includes(mix) ? current.filter((m) => m !== mix) : [...current, mix];
      return { ...r, type_mix: next };
    });
    setOperatingAreas(updated);
    updateFields({ operating_areas: updated });
  };

  const handleContinue = (e) => {
    e.preventDefault();
    updateFields({
      fleet_capacities: capacities,
      operating_areas: operatingAreas,
      operating_hours: formData.operating_hours || '24x7',
      primary_base_location: formData.primary_base_location || (formData.city && formData.state ? `${formData.city}, ${formData.state}` : formData.city || formData.registered_address || ''),
      primary_pincode: formData.primary_pincode || formData.pincode || ''
    });
    saveAndContinue();
  };

  return (
    <div className="w-full">
      {/* Header Row: Step info and Switch to Voice (Image 1) */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 block mb-1">
            STEP 2 OF 6
          </span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight leading-tight">
            Capacity & coverage
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Tell us where your fleet is usually available.
          </p>
          {/* Smart Ambulance Blue accent line (NOT red!) */}
          <div className="w-14 sm:w-16 h-0.5 bg-blue-600 mt-2 rounded-full" />
        </div>

        <button
          type="button"
          onClick={() => navigate('/hospital/onboarding/voice?type=fleet&step=2')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium shadow-2xs transition-colors self-start cursor-pointer"
        >
          <Mic className="w-3.5 h-3.5 text-slate-600" />
          <span>Switch to voice</span>
        </button>
      </div>

      <form onSubmit={handleContinue} className="space-y-6">
        {/* Card 1: Fleet capacity (Image 1) */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Fleet capacity</h3>
              <p className="text-xs text-slate-400">Total ambulances available by configuration</p>
            </div>
            <button
              type="button"
              onClick={handleAddCapacityRow}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 hover:border-slate-300 bg-white text-xs font-medium text-blue-600 hover:bg-blue-50/50 transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add row</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500 font-semibold">
                  <th className="py-2.5 px-3 w-10">#</th>
                  <th className="py-2.5 px-3 min-w-[200px]">Ambulance Type</th>
                  <th className="py-2.5 px-3 min-w-[120px]">Owned</th>
                  {/* Attached field - hidden per onboarding requirement
                  <th className="py-2.5 px-3 min-w-[120px]">Attached</th>
                  */}
                  <th className="py-2.5 px-3 min-w-[120px]">Available Daily</th>
                  <th className="py-2.5 px-3 w-10 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {capacities.map((row, idx) => (
                  <tr key={row.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-3 font-semibold text-slate-500">{idx + 1}</td>
                    <td className="py-3 px-3">
                      <div className="relative">
                        <select
                          value={row.ambulance_type}
                          onChange={(e) => handleCapacityChange(row.id, 'ambulance_type', e.target.value)}
                          title={row.ambulance_type || 'Select vehicle type'}
                          className="w-full py-2 pl-3 pr-8 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 appearance-none cursor-pointer"
                        >
                          <option value="">{loadingAmbulanceTypes ? 'Loading types...' : 'Select vehicle type'}</option>
                          {ambulanceTypeNames.map((t) => {
                            const abbr = getAmbulanceTypeAbbreviation(t) || t;
                            return (
                              <option key={t} value={abbr} title={t}>{abbr}</option>
                            );
                          })}
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <input
                        type="number"
                        min="0"
                        placeholder="Owned"
                        value={row.owned}
                        onChange={(e) => handleCapacityChange(row.id, 'owned', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </td>
                    {/* Attached field - hidden per onboarding requirement
                    <td className="py-3 px-3">
                      <input
                        type="number"
                        min="0"
                        placeholder="Attached"
                        value={row.attached}
                        onChange={(e) => handleCapacityChange(row.id, 'attached', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </td>
                    */}
                    <td className="py-3 px-3">
                      <input
                        type="number"
                        min="0"
                        placeholder="Available"
                        value={row.available_daily}
                        onChange={(e) => handleCapacityChange(row.id, 'available_daily', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 font-semibold"
                      />
                    </td>
                    <td className="py-3 px-3 text-center">
                      {capacities.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveCapacityRow(row.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Card 2: Operations & coverage (Commented out / Hidden)
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="pb-4 mb-5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Operations & coverage</h3>
            <p className="text-xs text-slate-400">Primary dispatch hub and operating jurisdiction</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Primary Base Location
              </label>
              <input
                type="text"
                placeholder="Enter primary base location"
                value={formData.primary_base_location || ''}
                onChange={(e) => updateFields({ primary_base_location: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Primary PIN Code
              </label>
              <input
                type="text"
                maxLength={6}
                placeholder="Enter primary pin code"
                value={formData.primary_pincode || ''}
                onChange={(e) => updateFields({ primary_pincode: e.target.value.replace(/\D/g, '') })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Typical Service Radius (KM)
              </label>
              <input
                type="number"
                placeholder="Enter typical service radius (km)"
                value={formData.service_radius_km || ''}
                onChange={(e) => updateFields({ service_radius_km: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Operating Hours
              </label>
              <div className="relative">
                <select
                  value={formData.operating_hours || ''}
                  onChange={(e) => updateFields({ operating_hours: e.target.value })}
                  className="w-full px-3.5 py-2.5 pr-8 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all appearance-none cursor-pointer"
                >
                  <option value="">Select operating hours</option>
                  <option value="24x7">24x7 Round the Clock</option>
                  <option value="Day Shift (6 AM - 8 PM)">Day Shift (6 AM - 8 PM)</option>
                  <option value="Night Shift (8 PM - 6 AM)">Night Shift (8 PM - 6 AM)</option>
                  <option value="Custom Shift">Custom Operational Shift</option>
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Intercity Available?
            </label>
            <div className="relative">
              <select
                value={formData.intercity_available || ''}
                onChange={(e) => updateFields({ intercity_available: e.target.value })}
                className="w-full px-3.5 py-2.5 pr-8 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all appearance-none cursor-pointer"
              >
                <option value="">Select intercity option</option>
                <option value="Yes - Dedicated">Yes - Dedicated Intercity Fleet</option>
                <option value="Yes - On Demand">Yes - On Demand Subject to Availability</option>
                <option value="No - Local Only">No - Local City Boundaries Only</option>
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>
        */}

        {/* Card 3: Top operating areas - Hidden per onboarding requirement
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Top operating areas</h3>
              <p className="text-xs text-slate-400">Regular coverage localities and ambulance availability</p>
            </div>
            <button
              type="button"
              onClick={handleAddAreaRow}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 hover:border-slate-300 bg-white text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add row</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500 font-semibold">
                  <th className="py-2.5 px-3 w-10">#</th>
                  <th className="py-2.5 px-3 min-w-[220px]">Area / Locality</th>
                  <th className="py-2.5 px-3 min-w-[110px]">PIN Code</th>
                  <th className="py-2.5 px-3 min-w-[130px]">Available Ambulances</th>
                  <th className="py-2.5 px-3 min-w-[240px]">Peak Availability</th>
                  <th className="py-2.5 px-3 min-w-[140px]">Type Mix</th>
                  <th className="py-2.5 px-3 w-10 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {operatingAreas.map((row, idx) => (
                  <tr key={row.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-3 font-medium text-slate-500 align-top pt-4">{idx + 1}</td>
                    <td className="py-3 px-3 align-top">
                      <AreaSearchInput
                        value={row.area || ''}
                        onChange={(val) => handleAreaChange(row.id, 'area', val)}
                        onSelectPlace={(placeData) => handleAreaSelectPlace(row.id, placeData)}
                      />
                    </td>
                    <td className="py-3 px-3 align-top">
                      <input
                        type="text"
                        maxLength={6}
                        placeholder="e.g. 380058"
                        value={row.pincode || ''}
                        onChange={(e) => handleAreaChange(row.id, 'pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 font-mono transition-all"
                      />
                    </td>
                    <td className="py-3 px-3 align-top">
                      <input
                        type="number"
                        min="0"
                        placeholder="Count"
                        value={row.available_ambulances}
                        onChange={(e) => handleAreaChange(row.id, 'available_ambulances', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </td>
                    <td className="py-3 px-3 align-top">
                      <div className="p-2 border border-slate-200 rounded-xl bg-slate-50/50 space-y-1">
                        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider block mb-1">
                          Select slots:
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {PEAK_SLOTS.map((slot) => {
                            const isChecked = (row.peak_availability || []).includes(slot);
                            return (
                              <button
                                key={slot}
                                type="button"
                                onClick={() => togglePeakSlot(row.id, slot)}
                                className={`px-2 py-0.5 rounded-md text-[11px] font-medium border flex items-center gap-1 transition-all ${
                                  isChecked
                                    ? 'bg-blue-50 border-blue-600 text-blue-700'
                                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                                }`}
                              >
                                <span className={`w-2.5 h-2.5 rounded-xs flex items-center justify-center border text-[8px] ${
                                  isChecked ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-300 bg-white'
                                }`}>
                                  {isChecked && '✓'}
                                </span>
                                <span>{slot}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 align-top">
                      <div className="p-2 border border-slate-200 rounded-xl bg-slate-50/50 space-y-1">
                        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider block mb-1">
                          Select types:
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {ambulanceTypeNames.map((mix) => {
                            const isChecked = (row.type_mix || []).includes(mix);
                            return (
                              <button
                                key={mix}
                                type="button"
                                onClick={() => toggleTypeMix(row.id, mix)}
                                className={`px-2 py-0.5 rounded-md text-[11px] font-medium border flex items-center gap-1 transition-all ${
                                  isChecked
                                    ? 'bg-blue-50 border-blue-600 text-blue-700'
                                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                                }`}
                              >
                                <span className={`w-2.5 h-2.5 rounded-xs flex items-center justify-center border text-[8px] ${
                                  isChecked ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-300 bg-white'
                                }`}>
                                  {isChecked && '✓'}
                                </span>
                                <span>{mix}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center align-top">
                      {operatingAreas.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveAreaRow(row.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer mt-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        */}

        {/* Bottom Form Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={goBack}
            className="px-5 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs sm:text-sm font-medium transition-colors cursor-pointer"
          >
            Back
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={saveAndExit}
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs sm:text-sm font-medium transition-colors cursor-pointer"
            >
              <Bookmark className="w-4 h-4 text-slate-500" />
              <span>{isSaving ? 'Saving...' : 'Save & exit'}</span>
            </button>

            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium shadow-md hover:shadow-lg transition-all cursor-pointer disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <span>Save & continue</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Step2CapacityCoverage;
